/**
 * Helpers for the Events pages: paths, ids, readable errors, dates and the event write payload.
 */

import {
  EventSourceIds,
  EventReviewStatusIds,
  EventSyncRunStatusIds,
  EventSourceMapKindIds,
  EventSourceMappingStatusIds,
  EventStatusIDs,
  type EventAdminDetail,
  type EventAdminListItem,
  type UpdateEventRequest,
} from '@actschurch/ccms-sdk';

// ---------------------------------------------------------------- paths

export const EVENTS_BASE = '/app/events';
export const EVENTS_LIST_PATH = `${EVENTS_BASE}/events`;
export const NEW_EVENT_PATH = `${EVENTS_BASE}/events/new`;
export const REVIEW_PATH = `${EVENTS_BASE}/review`;
export const SOURCES_PATH = `${EVENTS_BASE}/sources`;
export const MAPPINGS_PATH = `${EVENTS_BASE}/mappings`;
export const TYPES_PATH = `${EVENTS_BASE}/types`;
export const RULES_PATH = `${EVENTS_BASE}/rules`;
export const NEW_RULE_PATH = `${EVENTS_BASE}/rules/new`;
export const LEARNING_PATH = `${EVENTS_BASE}/learning`;
export const TAGS_PATH = `${EVENTS_BASE}/tags`;
export const rulePath = (id: number) => `${EVENTS_BASE}/rules/${id}`;
export const PLAYBOOKS_PATH = `${EVENTS_BASE}/playbooks`;
export const NEW_PLAYBOOK_PATH = `${EVENTS_BASE}/playbooks/new`;
export const playbookPath = (id: number) => `${EVENTS_BASE}/playbooks/${id}`;
/** The event detail page opened on its Plan tab. */
export const eventPlanPath = (id: number) => `${EVENTS_BASE}/events/${id}?tab=plan`;
/** The event detail page opened on its Connected tab. */
export const eventConnectedPath = (id: number) => `${EVENTS_BASE}/events/${id}?tab=connected`;
export const eventPath = (id: number) => `${EVENTS_BASE}/events/${id}`;
export const eventEditPath = (id: number) => `${EVENTS_BASE}/events/${id}/edit`;

/** Route param → positive id, or null. */
export function parseId(param: string | undefined | null): number | null {
  if (param == null || !/^\d+$/.test(param)) return null;
  const n = Number(param);
  return Number.isSafeInteger(n) && n > 0 ? n : null;
}

// ---------------------------------------------------------------- names + colours (fallbacks when lookups fail)

export const SOURCE_NAMES: Record<number, string> = {
  [EventSourceIds.Ccms]: 'CCMS',
  [EventSourceIds.PlanningCenterCalendar]: 'Planning Center',
  [EventSourceIds.Ics]: 'Calendar feed',
};
export const SOURCE_COLORS: Record<number, string> = {
  [EventSourceIds.Ccms]: 'blue',
  [EventSourceIds.PlanningCenterCalendar]: 'grape',
  [EventSourceIds.Ics]: 'cyan',
};

export const REVIEW_NAMES: Record<number, string> = {
  [EventReviewStatusIds.Ready]: 'Ready',
  [EventReviewStatusIds.NeedsReview]: 'Needs review',
};
export const REVIEW_COLORS: Record<number, string> = {
  [EventReviewStatusIds.Ready]: 'teal',
  [EventReviewStatusIds.NeedsReview]: 'orange',
};

export const RUN_STATUS_NAMES: Record<number, string> = {
  [EventSyncRunStatusIds.Running]: 'Running',
  [EventSyncRunStatusIds.Succeeded]: 'Succeeded',
  [EventSyncRunStatusIds.Failed]: 'Failed',
  [EventSyncRunStatusIds.Partial]: 'Partial',
};
export const RUN_STATUS_COLORS: Record<number, string> = {
  [EventSyncRunStatusIds.Running]: 'blue',
  [EventSyncRunStatusIds.Succeeded]: 'teal',
  [EventSyncRunStatusIds.Failed]: 'red',
  [EventSyncRunStatusIds.Partial]: 'yellow',
};

export const MAP_KIND_NAMES: Record<number, string> = {
  [EventSourceMapKindIds.LocationText]: 'Location',
  [EventSourceMapKindIds.Tag]: 'Tag',
  [EventSourceMapKindIds.Room]: 'Room',
};

export const MAPPING_STATUS_NAMES: Record<number, string> = {
  [EventSourceMappingStatusIds.Unmapped]: 'Unmapped',
  [EventSourceMappingStatusIds.Mapped]: 'Mapped',
  [EventSourceMappingStatusIds.Ignored]: 'Ignored',
  [EventSourceMappingStatusIds.ExcludeEvents]: 'Excludes events',
};
export const MAPPING_STATUS_COLORS: Record<number, string> = {
  [EventSourceMappingStatusIds.Unmapped]: 'orange',
  [EventSourceMappingStatusIds.Mapped]: 'teal',
  [EventSourceMappingStatusIds.Ignored]: 'gray',
  [EventSourceMappingStatusIds.ExcludeEvents]: 'red',
};

export const STATUS_NAMES: Record<number, string> = {
  [EventStatusIDs.DRAFT]: 'Draft',
  [EventStatusIDs.SCHEDULED]: 'Scheduled',
  [EventStatusIDs.ONGOING]: 'Ongoing',
  [EventStatusIDs.COMPLETED]: 'Completed',
  [EventStatusIDs.CANCELLED]: 'Cancelled',
  [EventStatusIDs.POSTPONED]: 'Postponed',
  [EventStatusIDs.ARCHIVED]: 'Archived',
};
export const STATUS_COLORS: Record<number, string> = {
  [EventStatusIDs.DRAFT]: 'gray',
  [EventStatusIDs.SCHEDULED]: 'blue',
  [EventStatusIDs.ONGOING]: 'teal',
  [EventStatusIDs.COMPLETED]: 'green',
  [EventStatusIDs.CANCELLED]: 'red',
  [EventStatusIDs.POSTPONED]: 'orange',
  [EventStatusIDs.ARCHIVED]: 'gray',
};

// ---------------------------------------------------------------- event state

/** Came from an outside source (Planning Center and so on). */
export const isSynced = (e: Pick<EventAdminListItem, 'sourceID'> | null | undefined) =>
  !!e && (e.sourceID ?? EventSourceIds.Ccms) !== EventSourceIds.Ccms;

/** The source still owns the schedule fields (synced and not detached). */
export const isSourceOwned = (e: Pick<EventAdminListItem, 'sourceID' | 'isDetached'> | null | undefined) =>
  isSynced(e) && !e?.isDetached;

export const isCancelled = (e: Pick<EventAdminListItem, 'statusID'>) => e.statusID === EventStatusIDs.CANCELLED;
export const isHeld = (e: Pick<EventAdminListItem, 'reviewStatusID'>) => e.reviewStatusID === EventReviewStatusIds.NeedsReview;

export function sourceName(id: number | null | undefined, name?: string | null): string {
  return name || SOURCE_NAMES[id ?? EventSourceIds.Ccms] || 'Unknown source';
}

/** "All campuses", the campus name, or "Not set". */
export function campusLabel(e: Pick<EventAdminListItem, 'allCampuses' | 'locationName' | 'locationID'>): string {
  if (e.allCampuses) return 'All campuses';
  return e.locationName || 'Not set';
}

// ---------------------------------------------------------------- errors

interface SdkErrorShape {
  message?: string;
  status?: number;
  statusCode?: number;
  code?: string;
  errors?: unknown;
}

export function errorStatus(err: unknown): number | undefined {
  const e = err as SdkErrorShape | null;
  return e?.status ?? e?.statusCode ?? undefined;
}

function errorList(err: unknown): string[] {
  const e = err as SdkErrorShape | null;
  return Array.isArray(e?.errors) ? (e!.errors as unknown[]).filter((x): x is string => typeof x === 'string') : [];
}

/** One readable sentence for a failed call. */
export function errorMessage(err: unknown, fallback = 'Something went wrong. Please try again.'): string {
  if (!err) return fallback;
  const status = errorStatus(err);
  const e = err as SdkErrorShape;
  if (status === 400) {
    const parts = errorList(err).map((x) => {
      const idx = x.indexOf(':');
      return idx > 0 ? x.slice(idx + 1).trim() : x;
    });
    return parts.length ? parts.join(' ') : e.message || fallback;
  }
  if (status === 401) return 'Your session has expired. Sign in again.';
  if (status === 403) return 'You do not have permission to do this.';
  if (status === 404) return e.message || 'It could not be found. It may not be set up on the server yet.';
  if (status === 408) return 'The server took too long to answer. Check your connection and try again.';
  if (e?.code === 'INVALID_RESPONSE') return 'The Events service did not answer as expected. It may not be deployed yet.';
  if (e?.code === 'NETWORK_ERROR') return 'Could not reach the server. Check your connection and try again.';
  return e?.message || fallback;
}

// ---------------------------------------------------------------- numbers + text

const numberFormat = new Intl.NumberFormat('en-ZA');
export const formatNumber = (n: number | null | undefined) => numberFormat.format(Number(n ?? 0));
export const plural = (n: number, one: string, many?: string) => `${formatNumber(n)} ${n === 1 ? one : many ?? `${one}s`}`;

const randFormat = new Intl.NumberFormat('en-ZA', { minimumFractionDigits: 0, maximumFractionDigits: 2 });
/** "R 5 000" or "R 1 250,50" (Stage 3 budget and purchase request amounts). */
export const formatRand = (n: number | null | undefined) => `R ${randFormat.format(Number(n ?? 0))}`;

// ---------------------------------------------------------------- dates

const pad = (n: number) => String(n).padStart(2, '0');

/** Local Date → yyyy-MM-dd. */
export function dateToYmd(d: Date | null | undefined): string | undefined {
  if (!d || Number.isNaN(d.getTime())) return undefined;
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/** yyyy-MM-dd → local Date at midnight. */
export function ymdToDate(ymd: string | null | undefined): Date | null {
  if (!ymd) return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(ymd);
  if (!m) return null;
  return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
}

export function addDays(d: Date, days: number): Date {
  const out = new Date(d);
  out.setDate(out.getDate() + days);
  return out;
}

export function startOfToday(): Date {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d;
}

export function parseIso(iso: string | null | undefined): Date | null {
  if (!iso) return null;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? null : d;
}

/** Mantine date inputs may hand back a Date or a string, depending on the version. */
export function toDate(v: unknown): Date | null {
  if (v == null || v === '') return null;
  const d = v instanceof Date ? v : new Date(String(v));
  return Number.isNaN(d.getTime()) ? null : d;
}

const DATE_FMT: Intl.DateTimeFormatOptions = { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' };
const SHORT_FMT: Intl.DateTimeFormatOptions = { day: 'numeric', month: 'short', year: 'numeric' };
const TIME_FMT: Intl.DateTimeFormatOptions = { hour: '2-digit', minute: '2-digit', hour12: false };

export function formatDate(d: Date | null, fallback = 'Not set'): string {
  return d ? d.toLocaleDateString('en-ZA', DATE_FMT) : fallback;
}

export function formatShortDate(d: Date | null, fallback = 'Not set'): string {
  return d ? d.toLocaleDateString('en-ZA', SHORT_FMT) : fallback;
}

export function formatTime(d: Date | null): string {
  return d ? d.toLocaleTimeString('en-ZA', TIME_FMT) : '';
}

export function formatDateTime(iso: string | null | undefined, fallback = 'Not recorded'): string {
  const d = parseIso(iso);
  return d ? `${d.toLocaleDateString('en-ZA', SHORT_FMT)}, ${formatTime(d)}` : fallback;
}

/** "5 minutes ago", "3 hours ago", "2 days ago". */
export function timeAgo(iso: string | null | undefined, fallback = 'Never'): string {
  const d = parseIso(iso);
  if (!d) return fallback;
  const mins = Math.round((Date.now() - d.getTime()) / 60_000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${plural(mins, 'minute')} ago`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `${plural(hours, 'hour')} ago`;
  return `${plural(Math.round(hours / 24), 'day')} ago`;
}

/** Event start as a local Date (prefers the server's church-time fields). */
export function eventStart(e: Pick<EventAdminListItem, 'startDate' | 'localDate' | 'localStartTime' | 'isAllDay'>): Date | null {
  if (e.localDate) {
    const day = ymdToDate(e.localDate);
    if (day && e.localStartTime && !e.isAllDay) {
      const [h, m] = e.localStartTime.split(':').map(Number);
      day.setHours(h || 0, m || 0, 0, 0);
    }
    return day;
  }
  return parseIso(e.startDate);
}

/** "Sun, 19 Oct 2026 at 09:00", "Sun, 19 Oct 2026 (all day)" or a date range for multi-day events. */
export function formatWhen(e: Pick<EventAdminListItem, 'startDate' | 'endDate' | 'localDate' | 'localStartTime' | 'isAllDay'>): string {
  const start = eventStart(e);
  if (!start) return 'Date not set';
  const end = parseIso(e.endDate);
  const multiDay = !!end && dateToYmd(end) !== dateToYmd(parseIso(e.startDate) ?? start) && end.getTime() - start.getTime() > 24 * 3600_000;
  if (multiDay && end) return `${formatShortDate(start)} to ${formatShortDate(end)}${e.isAllDay ? '' : `, from ${formatTime(start)}`}`;
  if (e.isAllDay) return `${formatDate(start)} (all day)`;
  return `${formatDate(start)} at ${e.localStartTime || formatTime(start)}`;
}

// ---------------------------------------------------------------- write payload

/**
 * Full PUT body for api/events/{id}, built from the stored event plus the changes.
 *
 * The server merges (null = keep), but sending every stored value keeps older full-replace builds
 * from wiping columns. Source-owned fields are sent unchanged unless the event is detached or manual.
 */
export function buildUpdatePayload(
  detail: EventAdminDetail,
  changes: Partial<UpdateEventRequest> = {}
): UpdateEventRequest {
  const base: UpdateEventRequest = {
    eventID: detail.eventID,
    locationID: detail.allCampuses ? 0 : detail.locationID,
    typeID: detail.typeID,
    statusID: detail.statusID,
    name: detail.name,
    description: detail.description ?? null,
    details: detail.details ?? null,
    thumbnailUrl: detail.thumbnailUrl || null,
    bannerUrl: detail.bannerUrl || null,
    startDate: detail.startDate,
    endDate: detail.endDate,
    showInRides: detail.showInRides,
    isPublic: detail.isPublic,
    isAllDay: detail.isAllDay,
    allCampuses: detail.allCampuses,
    venueID: detail.venueID ?? null,
  };
  const merged = { ...base, ...changes, eventID: detail.eventID };
  if (isSourceOwned(detail)) {
    // The source owns these: never send edited values (the server would ignore or reject them).
    merged.name = base.name;
    merged.startDate = base.startDate;
    merged.endDate = base.endDate;
    merged.isAllDay = base.isAllDay;
    merged.description = base.description;
  }
  if (merged.allCampuses) {
    merged.locationID = 0;
  }
  return merged;
}
